import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import { LoadingStatus } from '../../../../../../core/domain/events/state_provider';
import {
  BillParams,
  BillParamsProps,
} from '../../../../../../data/dietitian/domain/bill/bill-params';
import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import {
  DietitianState,
  DietitianStateProvider,
} from '../../../../../../data/dietitian/domain/dietitian_state_provider';
import { BillParamsEventProvider } from '../../../../../../data/dietitian/domain/events/bill/bill-params-event-provider';
import {
  BillParamsEvent,
  BillParamsLogoDeleted,
  BillParamsLogoSaved,
  BillParamsSaved,
  BillParamsSignDeleted,
  BillParamsSignSaved,
} from '../../../../../../data/dietitian/domain/events/bill/bill-params-events';
import { Picture } from '../../../../../../data/dietitian/domain/picture/picture';
import { BillRepository } from '../../../../../../data/dietitian/repositories/bill.repository';
import { BillParamsRepository } from '../../../../../../data/dietitian/repositories/bill-params.repository';
import { DietitianRepository } from '../../../../../../data/dietitian/repositories/dietitian_repository';

@Injectable()
export class AccountBillParamsFacade {
  private subscription: Subscription;

  constructor(
    private dietitianStateProvider: DietitianStateProvider,
    private billParamsEventProvider: BillParamsEventProvider,
    private billParamsRepository: BillParamsRepository,
    private billRepository: BillRepository,
    private dietitianRepository: DietitianRepository,
  ) {
    // Nada
  }

  destroy() {
    this.subscription?.unsubscribe();
  }

  get dietitianState$(): Observable<DietitianState> {
    return this.dietitianStateProvider.state$;
  }

  get billParamsEventProvider$(): Observable<BillParamsEvent> {
    return this.billParamsEventProvider.events$;
  }

  async updateLogo(file: File, billParams: BillParams): Promise<BillParams> {
    const billParamsPros = billParams.props;
    billParamsPros.logo = Picture.create({
      file,
      path: `/users/${billParamsPros.dietitianId.id.toString()}/logo`,
    });
    const savedBillParams = await this.billParamsRepository.save(
      BillParams.create(billParamsPros),
    );
    this.billParamsEventProvider.dispatch(
      new BillParamsLogoSaved(savedBillParams, true),
    );
    return savedBillParams;
  }

  async updateSign(file: File, billParams: BillParams): Promise<BillParams> {
    const billParamsPros = billParams.props;
    billParamsPros.sign = Picture.create({
      file,
      path: `/users/${billParamsPros.dietitianId.id.toString()}/sign`,
    });
    const savedBillParams = await this.billParamsRepository.save(
      BillParams.create(billParamsPros),
    );
    this.billParamsEventProvider.dispatch(
      new BillParamsSignSaved(savedBillParams, true),
    );
    return savedBillParams;
  }

  async deleteLogo(billParams: BillParamsProps): Promise<BillParams> {
    billParams.logo = undefined;
    billParams.logoPosition = undefined;
    billParams.logoSize = undefined;
    const savedBillParams = await this.billParamsRepository.save(
      BillParams.create(billParams),
    );
    this.billParamsEventProvider.dispatch(
      new BillParamsLogoDeleted(savedBillParams, true),
    );
    return savedBillParams;
  }

  async deleteSign(billParams: BillParamsProps): Promise<BillParams> {
    billParams.sign = undefined;
    const savedBillParams = await this.billParamsRepository.save(
      BillParams.create(billParams),
    );
    this.billParamsEventProvider.dispatch(
      new BillParamsSignDeleted(savedBillParams, true),
    );
    return savedBillParams;
  }

  async save(billParams: BillParams): Promise<BillParams> {
    const savedBillParams = await this.billParamsRepository.save(billParams);
    this.billParamsEventProvider.dispatch(
      new BillParamsSaved(savedBillParams, true),
    );
    return savedBillParams;
  }

  async load(dietitianId: string): Promise<BillParams> {
    return this.billParamsRepository.load(dietitianId);
  }

  async previewPdf(): Promise<string> {
    return this.billRepository.previewPdfFromCloud();
  }

  async saveDietitian(dietitian: Dietitian) {
    const updatedDietitian = await this.dietitianRepository.save(dietitian);
    this.dietitianStateProvider.setState(<DietitianState>{
      loading: LoadingStatus.COMPLETE,
      origin: 'AccountBillParamsFacade.saveDietitian',
      entity: updatedDietitian,
    });
    return dietitian;
  }
}
